import { CSSProperties, ReactNode, useMemo } from 'react';
import { Replay, Tick, Tier } from '../model/Replay';
import { Icon } from './Icons';

// ============ Logo ============
export function TMLogo({ size = 28 }: { size?: number }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: 'var(--font-mono)',
        fontWeight: 700,
        fontSize: size * 0.5,
        letterSpacing: '0.04em',
        color: 'var(--fg-1)',
      }}
    >
      <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
        <rect x="0" y="0" width="32" height="32" rx="6" fill="var(--accent)" />
        <path d="M6 11 L 16 21 L 26 11 L 22 11 L 16 17 L 10 11 Z" fill="#051019" />
        <rect x="6" y="22" width="20" height="2.5" fill="#051019" opacity="0.85" />
      </svg>
      <span>TM·UTILS</span>
    </div>
  );
}

// ============ Tier badge ============
export function TierBadge({
  tier = 'S',
  size = 'md',
  label = true,
  title,
}: {
  tier?: Tier;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  label?: boolean;
  title?: string;
}) {
  const tierColors: Record<Tier, string> = {
    S: 'var(--tier-s)',
    A: 'var(--tier-a)',
    B: 'var(--tier-b)',
    C: 'var(--tier-c)',
    D: 'var(--tier-d)',
    E: 'var(--tier-e)',
    F: 'var(--tier-f)',
  };
  const c = tierColors[tier] || 'var(--fg-3)';
  const sizes = {
    sm: { h: 22, stripe: 4, fs: 11, pl: 8, pr: 10, lpad: 6 },
    md: { h: 28, stripe: 5, fs: 13, pl: 10, pr: 12, lpad: 8 },
    lg: { h: 40, stripe: 7, fs: 18, pl: 14, pr: 16, lpad: 10 },
    xl: { h: 56, stripe: 9, fs: 26, pl: 18, pr: 22, lpad: 14 },
  } as const;
  const s = sizes[size];
  return (
    <div
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: s.h,
        border: `1px solid ${c}`,
        borderRadius: 'var(--r-4)',
        fontFamily: 'var(--font-mono)',
        fontWeight: 700,
        color: c,
        fontSize: s.fs,
        letterSpacing: '0.04em',
        background: 'rgba(0,0,0,0.2)',
        paddingRight: label ? s.pr : s.pl,
      }}
    >
      <div style={{ width: s.stripe, height: '100%', background: c, marginRight: s.lpad }} />
      <span>{tier}</span>
      {label && size !== 'sm' && <span style={{ marginLeft: 6, opacity: 0.6, fontWeight: 500 }}>TIER</span>}
    </div>
  );
}

// ============ Stat block ============
export function Stat({
  label,
  value,
  unit,
  accent,
  mono = true,
  align = 'left',
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  accent?: string;
  mono?: boolean;
  align?: 'left' | 'right';
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: align }}>
      <div className="eyebrow">{label}</div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 4,
          justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        }}
      >
        <span
          style={{
            fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 22,
            color: accent || 'var(--fg-1)',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.01em',
          }}
        >
          {value}
        </span>
        {unit && (
          <span style={{ color: 'var(--fg-3)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>{unit}</span>
        )}
      </div>
    </div>
  );
}

// ============ Chip / Button ============
export function Chip({
  children,
  active = false,
  onClick,
  accent,
  icon,
  style,
}: {
  children?: ReactNode;
  active?: boolean;
  onClick?: () => void;
  accent?: string;
  icon?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 28,
        padding: '0 12px',
        background: active ? accent || 'var(--accent-12)' : 'transparent',
        border: `1px solid ${active ? accent || 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 'var(--r-6)',
        color: active ? 'var(--fg-1)' : 'var(--fg-2)',
        fontSize: 12,
        fontFamily: 'var(--font-sans)',
        fontWeight: 500,
        cursor: 'pointer',
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  style,
  onClick,
}: {
  children?: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: ReactNode;
  style?: CSSProperties;
  onClick?: () => void;
}) {
  const variants = {
    primary: { bg: 'var(--accent)', fg: 'var(--fg-on-accent)', border: 'transparent' },
    secondary: { bg: 'var(--bg-surface-2)', fg: 'var(--fg-1)', border: 'var(--border-strong)' },
    ghost: { bg: 'transparent', fg: 'var(--fg-2)', border: 'var(--border)' },
    danger: { bg: 'var(--bad)', fg: '#220006', border: 'transparent' },
  } as const;
  const v = variants[variant];
  const sizes = {
    sm: { h: 26, px: 10, fs: 12 },
    md: { h: 36, px: 16, fs: 13 },
    lg: { h: 44, px: 20, fs: 14 },
  } as const;
  const s = sizes[size];
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        height: s.h,
        padding: `0 ${s.px}px`,
        background: v.bg,
        color: v.fg,
        border: `1px solid ${v.border}`,
        borderRadius: 'var(--r-6)',
        fontSize: s.fs,
        fontFamily: 'var(--font-sans)',
        fontWeight: 600,
        cursor: 'pointer',
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
}

// ============ Card ============
export function Card({
  children,
  style,
  padded = true,
  title,
  subtitle,
  eyebrow,
  actions,
}: {
  children?: ReactNode;
  style?: CSSProperties;
  padded?: boolean;
  title?: ReactNode;
  subtitle?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
        ...style,
      }}
    >
      {(title || actions) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '14px 18px 12px',
            borderBottom: subtitle ? 'none' : '1px solid var(--border-subtle)',
          }}
        >
          <div>
            {eyebrow && <div className="eyebrow" style={{ marginBottom: 4 }}>{eyebrow}</div>}
            {title && <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' }}>{title}</div>}
            {subtitle && <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 2 }}>{subtitle}</div>}
          </div>
          {actions}
        </div>
      )}
      <div style={{ padding: padded ? 18 : 0 }}>{children}</div>
    </div>
  );
}

// ============ Mini sparkline ============
export function MiniSparkline({
  ticks,
  dimension = 'speed',
  height = 28,
  color = 'var(--accent)',
}: {
  ticks: Tick[];
  dimension?: keyof Tick;
  height?: number;
  color?: string;
}) {
  const path = useMemo(() => {
    if (!ticks || !ticks.length) return '';
    const N = Math.min(ticks.length, 80);
    const step = ticks.length / N;
    const samples: number[] = [];
    for (let i = 0; i < N; i++) {
      samples.push(Number(ticks[Math.floor(i * step)][dimension]));
    }
    const min = Math.min(...samples), max = Math.max(...samples);
    return samples
      .map((v, i) => {
        const x = (i / (N - 1)) * 100;
        const y = max === min ? 50 : 100 - ((v - min) / (max - min)) * 100;
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join(' ');
  }, [ticks, dimension]);
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ============ Replay summary card ============
export function ReplayCard({
  replay,
  compact = false,
  style,
  onClick,
}: {
  replay: Replay;
  compact?: boolean;
  style?: CSSProperties;
  onClick?: () => void;
}) {
  const { meta, data } = replay;
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
        padding: compact ? 12 : 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        cursor: onClick ? 'pointer' : 'default',
        minWidth: 200,
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--fg-3)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {meta.map}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)', marginTop: 2 }}>{meta.author}</div>
        </div>
        <TierBadge tier={meta.tier} size="sm" label={false} />
      </div>
      <MiniSparkline ticks={data.ticks} dimension="speed" height={28} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div className="num" style={{ fontSize: 18, fontWeight: 600, color: 'var(--fg-1)' }}>
          {meta.raceTimeStr}
        </div>
        <div style={{ fontSize: 11, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)' }}>
          {meta.topSpeedKmh} km/h
        </div>
      </div>
    </div>
  );
}

// ============ EmptyState ============
export function EmptyState({
  title,
  body,
  icon,
  cta,
  accent = 'var(--accent)',
}: {
  title: ReactNode;
  body: ReactNode;
  icon?: ReactNode;
  cta?: ReactNode;
  accent?: string;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px dashed var(--border-strong)',
        borderRadius: 'var(--r-8)',
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 'var(--r-full)',
          background: 'var(--bg-surface-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: accent,
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' }}>{title}</div>
      <div className="p" style={{ fontSize: 12, color: 'var(--fg-3)', maxWidth: 260 }}>
        {body}
      </div>
      {cta && <Button variant="secondary" size="sm" style={{ marginTop: 4 }}>{cta}</Button>}
    </div>
  );
}

// ============ ParseProgress ============
export function ParseProgress({
  steps,
}: {
  steps: { label: string; done?: boolean; active?: boolean }[];
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
        padding: 20,
      }}
    >
      <div className="eyebrow" style={{ marginBottom: 14 }}>Parsing replay…</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {steps.map((s, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              color: s.done ? 'var(--fg-1)' : s.active ? 'var(--accent)' : 'var(--fg-muted)',
            }}
          >
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                border: `1.5px solid ${s.done ? 'var(--ok)' : s.active ? 'var(--accent)' : 'var(--border-strong)'}`,
                background: s.done ? 'var(--ok)' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#051019',
                position: 'relative',
              }}
            >
              {s.done && <Icon.Check />}
              {s.active && (
                <div
                  style={{
                    position: 'absolute',
                    inset: -3,
                    borderRadius: '50%',
                    border: '1.5px solid var(--accent)',
                    borderTopColor: 'transparent',
                    animation: 'tm-spin 0.8s linear infinite',
                  }}
                />
              )}
            </div>
            <span>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
